import AssignmentEditorHeader from 'components/CreateAssignment/AssignmentEditorHeader';
import AssignmentFeedbackPanel from 'components/CreateAssignment/AssignmentFeedbackPanel';
import AssignmentGradingPanel from 'components/CreateAssignment/AssignmentGradingPanel';
import AssignmentInlineCommentsPanel from 'components/CreateAssignment/AssignmentInlineCommentsPanel';
import AssignmentValidationSummary from 'components/CreateAssignment/AssignmentValidationSummary';
import GeneralSection from 'components/CreateAssignment/GeneralSection';
import { useDialog } from 'contexts/dialog';
import { useViewport } from 'contexts/viewport';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Box from 'shell/components/Box';
import Button from 'shell/components/Button';
import Container from 'shell/components/Container';
import Icon from 'shell/components/Icon';
import Stack from 'shell/components/Stack';
import Tabs from 'shell/components/Tabs';
import Text from 'shell/components/Text';
import TextInput from 'shell/components/TextInput';
import {
    ASSIGNMENT_EDITOR_ACTION,
    ASSIGNMENT_SETTINGS_TAB,
    ASSIGNMENT_VALIDATION_REASON,
    ASSIGNMENT_VALIDATION_RESULT,
    getAssignmentValidationItems,
    getFirstInvalidAssignmentTab,
    hasAssignmentSettingsTabError,
} from 'utils/assignmentEditorConstants';
import { AUTOSAVE_STATUS } from 'utils/autosave';
import { ViewportDevice } from 'utils/constants';

const tabLabel = (label, hasError, errorLabel) =>
    hasError ? (
        <Box
            component={'span'}
            aria-label={`${label}, ${errorLabel}`}
            sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: (theme) => theme.spacing(1.5),
            }}
        >
            <span>{label}</span>
            <Box
                component={'span'}
                aria-hidden={'true'}
                sx={{
                    flexShrink: 0,
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    background: (theme) => theme.palette.ensightful.error[600],
                }}
            />
        </Box>
    ) : (
        label
    );

const AssignmentSettingsEditor = ({
    batch,
    submissions,
    form,
    setForm,
    errors = {},
    setErrors,
    onClickBack,
    onClickCancel = onClickBack,
    onClickSave,
    onClickPublish,
    isNew = false,
    isDraft = false,
    isPersisted = !isNew,
    loading = false,
    isSaving = false,
    autosaveStatus = AUTOSAVE_STATUS.IDLE,
    lastSavedAt,
    onClickDelete,
    settingsRevision = 0,
    setSelectRubricDialog,
    setEditRubricDialog,
    setUploadContentBankResourcesDialog,
    setEditContentBankResourcesDialog,
    handleResyncAPIAssignment,
    forceRTEUpdate,
    onSettingsReplaced,
    onClickImportAssignmentSettings,
}) => {
    const { device } = useViewport();
    const { closeDialog, setDialog } = useDialog();
    const { t } = useTranslation('assignmentSettings');
    const tabErrorLabel = t('tabs.has-errors', 'Has errors');
    const [tab, setTab] = useState(ASSIGNMENT_SETTINGS_TAB.DESCRIPTION);
    const [hasPendingDraft, setHasPendingDraft] = useState(false);
    const [stickyHeaderHeight, setStickyHeaderHeight] = useState(0);
    const isEditable = !submissions?.length && !batch?.length;
    const busy = loading || isSaving;
    const autosaveStatusContent =
        autosaveStatus === AUTOSAVE_STATUS.SAVING ? (
            <Stack direction={'row'} alignItems={'center'} spacing={2} aria-live={'polite'}>
                <Text color={'light'}>{t('autosave.saving', 'Saving...')}</Text>
            </Stack>
        ) : [AUTOSAVE_STATUS.PENDING, AUTOSAVE_STATUS.ERROR].includes(autosaveStatus) ||
          (!isPersisted && autosaveStatus === AUTOSAVE_STATUS.IDLE) ? (
            <Button
                variant={'borderless'}
                disabled={busy}
                onClick={() => handleSave(ASSIGNMENT_EDITOR_ACTION.SAVE)}
                aria-label={
                    autosaveStatus === AUTOSAVE_STATUS.ERROR
                        ? t('autosave.save-failed', 'Save failed. Save')
                        : t('autosave.save', 'Save')
                }
            >
                {t('autosave.save', 'Save')}
            </Button>
        ) : autosaveStatus === AUTOSAVE_STATUS.SAVED ? (
            <Stack direction={'row'} alignItems={'center'} spacing={1} aria-live={'polite'}>
                <Icon kind={'checkmark'} />
                <Text color={'light'}>{t('autosave.saved', 'Saved')}</Text>
            </Stack>
        ) : null;
    // Publication is a single mutually exclusive action: drafts can be published, published assignments can be unpublished.
    const publicationAction =
        isPersisted && !isDraft ? ASSIGNMENT_EDITOR_ACTION.UNPUBLISH : ASSIGNMENT_EDITOR_ACTION.PUBLISH;
    const commonPanelProps = {
        form,
        setForm,
        errors,
        setErrors,
        isEditable,
    };
    const generalPanelProps = {
        ...commonPanelProps,
        setSelectRubricDialog,
        setEditRubricDialog,
        setUploadContentBankResourcesDialog,
        setEditContentBankResourcesDialog,
        handleResyncAPIAssignment,
        forceRTEUpdate,
        onSettingsReplaced,
        onClickImportAssignmentSettings,
    };
    const openValidationDialog = (action, validationErrors, targetTab) => {
        const isPendingDraft = action === ASSIGNMENT_VALIDATION_REASON.PENDING_DRAFT;
        const validationItems = getAssignmentValidationItems(validationErrors, t);
        const reviewTab = targetTab || ASSIGNMENT_SETTINGS_TAB.DESCRIPTION;
        const review = () => {
            closeDialog();
            window.setTimeout(() => document.getElementById(`tab-${reviewTab}`)?.focus(), 0);
        };

        setDialog({
            open: true,
            message: isPendingDraft
                ? t('validation-dialog.pending-draft-title', 'Finish your grading instruction')
                : action === ASSIGNMENT_EDITOR_ACTION.PUBLISH
                  ? t(
                        'validation-dialog.publish-title',
                        'Please complete all missing required fields before publishing.'
                    )
                  : t('validation-dialog.save-title', 'Please complete all missing required fields before saving.'),
            description: isPendingDraft ? (
                t(
                    'validation-dialog.pending-draft-description',
                    'Add or cancel your instruction draft before continuing.'
                )
            ) : (
                <AssignmentValidationSummary items={validationItems} />
            ),
            note: '',
            variant: isPendingDraft ? 'primary' : 'destructive',
            actionButtonText: isPendingDraft
                ? t('validation-dialog.review-grading', 'Review grading settings')
                : t('common:controls.ok', 'Ok'),
            cancelButtonText: isPendingDraft ? '' : t('common:controls.cancel', 'Cancel'),
            onAction: review,
            onCancel: review,
            onClose: review,
        });
    };
    const handleSave = async (key) => {
        if (hasPendingDraft) {
            setTab(ASSIGNMENT_SETTINGS_TAB.GRADING);
            openValidationDialog(ASSIGNMENT_VALIDATION_REASON.PENDING_DRAFT, {}, ASSIGNMENT_SETTINGS_TAB.GRADING);
            return;
        }
        if (key === ASSIGNMENT_EDITOR_ACTION.SAVE && (isDraft || !isPersisted)) {
            await onClickSave({ key });
            return;
        }
        const result = await (key === ASSIGNMENT_EDITOR_ACTION.PUBLISH && onClickPublish
            ? onClickPublish()
            : onClickSave({ key }));
        if (result?.status === ASSIGNMENT_VALIDATION_RESULT.INVALID) {
            setErrors((current) => ({ ...current, ...result.errors }));
            const target = getFirstInvalidAssignmentTab(result.errors || {});
            setTab(target || ASSIGNMENT_SETTINGS_TAB.DESCRIPTION);
            openValidationDialog(key, result.errors || {}, target);
            return;
        }
    };
    const tabs = [
        {
            key: ASSIGNMENT_SETTINGS_TAB.DESCRIPTION,
            value: tabLabel(
                t('tabs.description', 'Description & Settings'),
                hasAssignmentSettingsTabError(ASSIGNMENT_SETTINGS_TAB.DESCRIPTION, errors),
                tabErrorLabel
            ),
            content: <GeneralSection {...generalPanelProps} />,
        },
        {
            key: ASSIGNMENT_SETTINGS_TAB.GRADING,
            value: tabLabel(
                t('tabs.grading', 'Grading Settings'),
                hasAssignmentSettingsTabError(ASSIGNMENT_SETTINGS_TAB.GRADING, errors),
                tabErrorLabel
            ),
            content: (
                <AssignmentGradingPanel
                    {...commonPanelProps}
                    draftResetKey={settingsRevision}
                    onPendingDraftChange={setHasPendingDraft}
                />
            ),
        },
        {
            key: ASSIGNMENT_SETTINGS_TAB.FEEDBACK,
            value: tabLabel(
                t('tabs.feedback', 'Feedback Settings'),
                hasAssignmentSettingsTabError(ASSIGNMENT_SETTINGS_TAB.FEEDBACK, errors),
                tabErrorLabel
            ),
            content: <AssignmentFeedbackPanel {...commonPanelProps} />,
        },
        {
            key: ASSIGNMENT_SETTINGS_TAB.FORMATIVE,
            value: tabLabel(
                t('tabs.formative', 'Formative Feedback'),
                hasAssignmentSettingsTabError(ASSIGNMENT_SETTINGS_TAB.FORMATIVE, errors),
                tabErrorLabel
            ),
            content: <AssignmentFeedbackPanel {...commonPanelProps} student />,
        },
        {
            key: ASSIGNMENT_SETTINGS_TAB.INLINE_COMMENTS,
            value: tabLabel(
                t('tabs.inline-comments', 'Inline Comments'),
                hasAssignmentSettingsTabError(ASSIGNMENT_SETTINGS_TAB.INLINE_COMMENTS, errors),
                tabErrorLabel
            ),
            content: <AssignmentInlineCommentsPanel form={form} setForm={setForm} isEditable={isEditable} />,
        },
    ];

    return (
        <Container
            device={device}
            sx={(theme) => {
                const contentPadding = theme.spacing(device === ViewportDevice.MOBILE ? 4 : 8);

                return {
                    '--assignment-editor-content-padding': contentPadding,
                    '--assignment-editor-content-negative-padding': `-${contentPadding}`,
                    background: theme.palette.ensightful.shades[0],
                    alignItems: 'flex-start',
                    minHeight: '100%',
                    overflowX: 'clip',
                    px: 'var(--assignment-editor-content-padding)',
                    pb: 16,
                    maxWidth: 'none !important',
                };
            }}
        >
            <Stack spacing={4} sx={{ width: '100%', minWidth: 0 }}>
                <AssignmentEditorHeader
                    device={device}
                    busy={busy}
                    isPersisted={isPersisted}
                    publicationAction={publicationAction}
                    autosaveStatusContent={autosaveStatusContent}
                    lastSavedAt={lastSavedAt}
                    onClickBack={onClickBack}
                    onClickCancel={onClickCancel}
                    onClickDelete={onClickDelete}
                    onAction={handleSave}
                    onHeightChange={setStickyHeaderHeight}
                />
                <Box sx={{ width: '100%', maxWidth: 900, mx: 'auto' }}>
                    <TextInput
                        inputProps={{ 'aria-label': t('title-label', 'Assignment title') }}
                        placeholder={t('title-placeholder', 'Enter assignment title')}
                        value={form.title}
                        error={Boolean(errors.title)}
                        disabled={busy || !isEditable}
                        onChange={(event) => setForm((current) => ({ ...current, title: event.target.value }))}
                        sx={(theme) => ({
                            width: '100%',
                            '&& > div > input': {
                                fontSize: '1.25rem',
                                fontWeight: theme.typography.sh1.fontWeight.semibold,
                                padding: theme.spacing(1, 3),
                            },
                            '&& > div > fieldset': { borderColor: errors.title ? undefined : 'transparent' },
                        })}
                    />
                </Box>
                {!isEditable && (
                    <Box
                        sx={(theme) => ({
                            width: '100%',
                            maxWidth: 900,
                            mx: 'auto',
                            p: 3,
                            borderRadius: '8px',
                            background: theme.palette.ensightful.warning[100],
                        })}
                    >
                        <Text>
                            {t(
                                'assignment:create-assignment.edit-restriction-message',
                                'Editing assignment details after upload is restricted to keep grades and feedback consistent. Duplicate the assignment to change these settings.'
                            )}
                        </Text>
                    </Box>
                )}
                <Tabs
                    aria-label={t('tabs-label', 'Assignment settings')}
                    appearance={'underline'}
                    dragToScroll={device !== ViewportDevice.DESKTOP}
                    fullWidth
                    keepMounted
                    options={tabs}
                    selected={tab}
                    onChange={setTab}
                    sx={{
                        width: 'auto',
                        mx: 'var(--assignment-editor-content-negative-padding)',
                        '& > .MuiTabs-root': (theme) => ({
                            margin: 0,
                            position: 'sticky',
                            top: `calc(${stickyHeaderHeight}px + ${theme.spacing(5)} + ${theme.spacing(2)})`,
                            zIndex: 3,
                            background: theme.palette.ensightful.shades[0],
                            boxShadow: `0 -${theme.spacing(2)} 0 ${theme.palette.ensightful.shades[0]}`,

                            '& .MuiTab-root': {
                                minWidth: 150,
                            },
                        }),
                        '&& .MuiTabs-flexContainer': {
                            padding: '0 var(--assignment-editor-content-padding)',
                        },
                        '& > [role="tabpanel"]': {
                            maxWidth: 900,
                            pt: 5,
                            px: device === ViewportDevice.MOBILE ? 4 : 8,
                            mx: 'auto',
                        },
                    }}
                />
            </Stack>
        </Container>
    );
};

export default AssignmentSettingsEditor;
